import { useState } from 'preact/hooks'
import { useLocation } from 'wouter-preact'
import { calculateWeightTrend, type WeightTrend } from '~/calculations/weightTrends'
import { AnchorPrompts } from '~/components/feedback/AnchorPrompts'
import { BackupReminder } from '~/components/feedback/BackupReminder'
import { PhaseEndNotice } from '~/components/feedback/PhaseEndNotice'
import { WeekChecklist } from '~/components/feedback/WeekChecklist'
import { SolaMark } from '~/components/identity/SolaMark'
import { Screen } from '~/components/layout/Screen'
import { Button } from '~/components/primitives/Button'
import { NumericField } from '~/components/primitives/Field'
import { InfoButton } from '~/components/primitives/InfoButton'
import { StatusCard } from '~/components/primitives/StatusCard'
import { useAsyncData } from '~/hooks/useAsyncData'
import { TERNARY_ANSWER_LABELS, TERNARY_ANSWERS, type TernaryAnswer } from '~/models/dailyCheck'
import { FITNESS_DOMAIN_LABELS } from '~/models/fitnessDomain'
import { PHASE_LABELS } from '~/models/phase'
import type { WeightDisplayUnit } from '~/models/settings'
import type { WeightEntryRecord } from '~/models/weightEntry'
import { NEXT_PHASE_ROUTE, PLAN_ROUTE } from '~/routes'
import type { CombinedOpinion } from '~/rules/combine'
import { dismissBackupReminder, getBackupReminder } from '~/services/backupReminderService'
import {
  type DailyNutritionPrompt,
  type DailyQuestion,
  getDailyNutritionPrompt,
  recordDailyCheck,
} from '~/services/dailyCheckService'
import { getDomainOpinions } from '~/services/domainOpinionService'
import { getNextPhaseState } from '~/services/nextPhaseService'
import { getOnboardingState } from '~/services/onboardingService'
import { getPhaseEndState } from '~/services/phaseService'
import { getProjectStartState } from '~/services/projectService'
import {
  type AnchorPrompt,
  deferAnchorToTomorrow,
  getAnchorPrompts,
  hasEnabledAnchors,
  snoozeAnchor,
} from '~/services/routineAnchorService'
import { getCurrentTrainingWeek } from '~/services/trainingWeekService'
import { getWeekChecklist } from '~/services/weekChecklistService'
import {
  getDailyStatus,
  getTrendObservations,
  saveWeight,
  todayFor,
} from '~/services/weightService'
import { useDatabase } from '~/storage/DatabaseContext'
import type { StorageError } from '~/storage/errors'
import { getActivePhase } from '~/storage/repositories/phaseRepository'
import { getSettings } from '~/storage/repositories/settingsRepository'
import { STORE } from '~/storage/schema'
import { unwrapDate } from '~/types/dates'
import type { Kilograms } from '~/types/units'
import {
  formatCalendarDate,
  formatPercentChange,
  formatWeight,
  formatWeightChange,
  formatWeightValue,
} from '~/utils/format'
import type { Result } from '~/utils/result'
import { SessionList } from './SessionList'
import styles from './TodayPage.module.css'

/**
 * Today — the execution surface (D-099).
 *
 * ────────────────────────────────────────────────────────────────────────────
 *  ONE DOMINANT ACTION, AND EVERYTHING ELSE IS QUIETER THAN IT
 * ────────────────────────────────────────────────────────────────────────────
 *
 * Until C6.3 this screen carried no recommendation at all — `DecisionPanel`
 * rendered only on the Review screen, so the app's opinion was two taps away and
 * arrived once a week. That was the single largest gap between what the product
 * claims and what it did: Project Sola is supposed to remember, organise and
 * prioritise so the user only has to perform the next useful action, and the
 * screen they open every day did not tell them what that action was.
 *
 * **The regions are in a fixed order and none of them disappears when empty.**
 * They collapse to a quiet line instead. A layout that reorders itself daily is
 * the opposite of a routine anchor, and a screen whose height depends on how many
 * areas the user picked cannot be learned by muscle memory.
 *
 * **One action, never one panel per domain.** The combining is
 * `combineDomainOpinions` in `rules/` — per-domain detail belongs on My Plan or
 * in the finished review, reached deliberately rather than stacked here.
 *
 * **No chart, no metric without an action attached, no score.** Progress owns the
 * charts; the anti-scoring rule owns the rest.
 */
export function TodayPage() {
  const db = useDatabase()
  const [, navigate] = useLocation()
  /* Declared with the other hooks, above every conditional return — a `useState`
     below one runs on some renders and not others. */
  const [anchorBusy, setAnchorBusy] = useState(false)

  const { state, reload } = useAsyncData(async () => {
    const now = new Date()
    const reference = todayFor(now)
    const [
      onboarding,
      daily,
      phase,
      settings,
      observations,
      phaseEnd,
      reminder,
      opinions,
      nutrition,
      anchors,
      anyAnchors,
      checklist,
      nextPhase,
      trainingWeek,
      startState,
    ] = await Promise.all([
      getOnboardingState(db),
      getDailyStatus(db, now),
      getActivePhase(db),
      getSettings(db, now),
      getTrendObservations(db, reference),
      getPhaseEndState(db, now),
      getBackupReminder(db, now),
      getDomainOpinions(db, now),
      getDailyNutritionPrompt(db, now),
      getAnchorPrompts(db, now),
      hasEnabledAnchors(db),
      getWeekChecklist(db, now),
      getNextPhaseState(db),
      /* Materialises the current week on first read, which is what D-105 §6 means
         by lazy: Today asking what today holds is the moment the week exists. */
      getCurrentTrainingWeek(db, now),
      /* Read once, here, rather than derived wherever the project is already to
         hand — every surface goes through this one accessor (D-107 §2). */
      getProjectStartState(db, now),
    ])
    // The calculation is pure and takes the reference date explicitly, so the
    // same stored data always yields the same trend.
    const trend = observations.ok ? calculateWeightTrend(observations.value, reference) : undefined
    return {
      onboarding,
      daily,
      phase,
      settings,
      trend,
      phaseEnd,
      reminder,
      opinions,
      nutrition,
      anchors,
      anyAnchors,
      checklist,
      nextPhase,
      trainingWeek,
      startState,
      now,
    }
  }, [
    STORE.profile,
    STORE.phases,
    STORE.goals,
    STORE.domainGoals,
    STORE.weightEntries,
    STORE.settings,
    STORE.meta,
    STORE.dailyChecks,
    STORE.nutritionPlans,
    STORE.weeklyReviews,
    /* An anchor prompt disappears when its task's own record appears, so this
       screen has to watch the stores those tasks write to. Without them a
       measurement recorded elsewhere leaves a stale prompt until a reload. */
    STORE.routineAnchors,
    STORE.measurementSessions,
    STORE.photos,
    STORE.strengthEntries,
    /* Completing, skipping or moving a session writes here, and the checklist's
       counts read it — so without this key the row updates and the week's own
       count beneath it does not. */
    STORE.trainingWeeks,
  ])

  if (state.status === 'loading') {
    return (
      <Screen title="Today" headerAccessory={<SolaMark size="sm" />}>
        <p>Loading…</p>
      </Screen>
    )
  }

  if (state.status === 'error') {
    return (
      <Screen title="Today" headerAccessory={<SolaMark size="sm" />}>
        <p role="alert">Project Sola could not read your data. {state.message}</p>
      </Screen>
    )
  }

  const {
    onboarding,
    daily,
    phase,
    settings,
    trend,
    phaseEnd,
    reminder,
    opinions,
    nutrition,
    anchors,
    anyAnchors,
    checklist,
    nextPhase,
    trainingWeek,
    startState,
    now,
  } = state.value

  async function dismissReminder() {
    await dismissBackupReminder(db, new Date())
  }

  /* Snooze and "not today" differ only in the value they write, which is what
     lets them be genuinely different controls without a durable reason field.
     Neither records that a prompt was deferred — see D-099 §B. */
  async function deferAnchor(prompt: AnchorPrompt, how: 'snooze' | 'notToday') {
    setAnchorBusy(true)
    await (how === 'snooze'
      ? snoozeAnchor(db, prompt.kind, new Date())
      : deferAnchorToTomorrow(db, prompt.kind, new Date()))
    setAnchorBusy(false)
  }

  if (!onboarding.ok || !daily.ok || !settings.ok) {
    const message = !onboarding.ok
      ? onboarding.error.message
      : !daily.ok
        ? daily.error.message
        : 'Settings could not be read.'
    return (
      <Screen title="Today" headerAccessory={<SolaMark size="sm" />}>
        <p role="alert">{message}</p>
      </Screen>
    )
  }

  /* Setup, not a phase (D-103 §2). A user with no active phase — because body
     composition was never activated, or because their phase ended — gets the
     ordinary screen. Everything below already renders conditionally, and
     `combineDomainOpinions` returns `nothingToSay` with the reason rather than
     silence, so the honest state is reachable without a gate. */
  if (onboarding.value.status === 'firstRun') {
    return <OnboardingPrompt />
  }

  /* A failed read shows no prompt rather than an error: the daily check is the
     smallest thing on this screen, and losing Today over it would cost the user
     the weight entry and the recommendation as well. */
  const nutritionPrompt = nutrition.ok ? nutrition.value : undefined

  /*
   * The project exists and has not begun (D-107 §4).
   *
   * ────────────────────────────────────────────────────────────────────────────
   *  THE SUPPRESSIONS BELOW ARE NOT THE GUARANTEE. THE SERVICES ARE.
   * ────────────────────────────────────────────────────────────────────────────
   *
   * `getWeekChecklist` and `getCurrentTrainingWeek` both materialise records as a
   * side effect of being read, so hiding their output here would still have
   * written a training week for a week the project has not reached. Both refuse
   * at the service; these gates only stop the screen depending on a service's
   * return value for its shape.
   *
   * What stays: the weight field, the trend, the anchors, the backup reminder and
   * the plan link. The user is living these days and the evidence is real —
   * recording is the half of the app that keeps working while the plan waits.
   */
  const waitingUntil =
    startState.ok && !startState.value.hasStarted ? startState.value.startsOn : undefined
  const waiting = waitingUntil !== undefined

  return (
    <Screen title="Today" headerAccessory={<SolaMark size="sm" />}>
      {!waiting && phaseEnd.ok ? (
        <PhaseEndNotice state={phaseEnd.value} onGoToDecision={() => navigate('/settings')} />
      ) : null}

      {/* The fourteen-day notice (D-084). Backing up happens in Settings, which
          is where the file is created and the last-backup date lives; sending
          the user there rather than duplicating the flow keeps one path to the
          one operation that protects everything. */}
      {reminder.ok ? (
        <BackupReminder
          reminder={reminder.value}
          busy={false}
          onBackUp={() => navigate('/settings')}
          onDismiss={() => void dismissReminder()}
        />
      ) : null}

      <p class={styles.date}>{formatCalendarDate(daily.value.date)}</p>

      {/* The one dominant action, above everything the user might merely read.
          `combineDomainOpinions` has already chosen it and capped the rest.
          Absent while waiting, which is also what keeps `phaseWeekNumber`'s
          caller in `rulesService` unreachable before the project starts. */}
      {waiting ? null : <NextAction combined={opinions.ok ? opinions.value.combined : undefined} />}

      {/* `getActivePhase` happily returns a phase that has not begun, so this
          needs the gate even though the phase record is perfectly valid. */}
      {!waiting && phase.ok && phase.value !== undefined ? (
        <p class={styles.phase}>
          Current phase: <strong>{PHASE_LABELS[phase.value.type]}</strong>
        </p>
      ) : null}

      {/* Waiting to start (D-107 §4). In the same slot and the same register as
          "No phase running" below, and mutually exclusive with it by
          construction — a project that has not begun has no phase to end. */}
      {waitingUntil === undefined ? null : (
        <section class={styles.betweenPhases} aria-labelledby="waiting-heading">
          <div class={styles.betweenPhasesHead}>
            <h2 id="waiting-heading" class={styles.betweenPhasesHeading}>
              Your project starts {formatCalendarDate(waitingUntil, { weekday: 'long' })}
            </h2>
            <InfoButton topic="the waiting project" title="Why nothing is scheduled yet">
              <p>
                Your first week, your training sessions and your weekly review all begin on{' '}
                {formatCalendarDate(waitingUntil)}. Project Sola waits until then rather than
                starting a week you did not plan to be in.
              </p>
              <p>
                Nothing is on hold that you can act on. Weigh-ins, measurements and photos are all
                recorded normally in the meantime, and they are kept — the day you started setting
                this up stays part of your project.
              </p>
            </InfoButton>
          </div>
          <p>
            Everything you record now is kept — weigh-ins, measurements and photos. You can change
            the start date or discard this project in Settings.
          </p>
          {/* Secondary. Today has one primary action and it is the day's weight
              (D-104 §9); this notice arrived uninvited. */}
          <Button variant="secondary" onClick={() => navigate('/settings')}>
            Change or discard this project
          </Button>
        </section>
      )}

      {/* Between phases (D-103 §3, §5). This is where the app used to offer
          "Set up Project Sola" — which wrote a second active project. It now
          names the state, says what still works, and points at the one decision
          that resolves it. Above the weight field because it explains why the
          rest of the screen is quieter than usual; the weight field still works
          and is still the thing to do today. */}
      {!waiting && nextPhase.ok && nextPhase.value.available ? (
        <section class={styles.betweenPhases} aria-labelledby="between-phases-heading">
          <div class={styles.betweenPhasesHead}>
            <h2 id="between-phases-heading" class={styles.betweenPhasesHeading}>
              No phase running
            </h2>
            <InfoButton topic="the paused review" title="Why the review is paused">
              <p>
                Project Sola has no phase to judge a week against, so it is not forming an opinion
                rather than guessing at one. Your project is still going.
              </p>
              <p>
                Everything you record is still kept — weigh-ins, measurements and photos all
                continue — and the weeks you have already reviewed are unchanged. Only the judging
                stops.
              </p>
            </InfoButton>
          </div>
          <p>Your project continues, and the weekly review waits for your next phase.</p>
          {/* Secondary, like every other notice on this screen: Today has one
              primary action and it is the day's own (D-104 §9). This is a
              decision waiting rather than the thing to do now, and the review
              being paused is not a reason to stop recording. */}
          <Button variant="secondary" onClick={() => navigate(NEXT_PHASE_ROUTE)}>
            Choose your next phase
          </Button>
        </section>
      ) : null}

      <WeightEntry
        key={daily.value.date}
        existingKg={daily.value.today?.weightKg}
        unit={settings.value.weightDisplayUnit}
        onSave={(weightKg) => saveWeight(db, { date: todayFor(new Date()), weightKg }, new Date())}
      />

      {/* Three taps, and only where there is something to tap (D-096). A user
          with no nutrition target sees nothing here at all — no empty task. */}
      {/* Suppressed while waiting, while the plan record stays effective from the
          day it was set up (D-107 §8). The targets are real — the user is eating
          — but asking whether a plan that has not begun was followed is a
          question about executing it. The record and the prompt part company
          here, and the record is where that argument lives. */}
      {nutritionPrompt === undefined || waiting ? null : (
        <DailyNutrition
          key={unwrapDate(nutritionPrompt.date)}
          prompt={nutritionPrompt}
          onAnswer={(field, answer) =>
            recordDailyCheck(
              db,
              nutritionPrompt.date,
              { nutrition: { [field]: answer } },
              new Date(),
            )
          }
        />
      )}

      {/* Below the weight field, because above it an anchor competes with the
          screen's dominant action; above the trend, because a trend is something
          to read and an anchor is something to do. Present whenever the user has
          configured any routine at all, saying "nothing open right now" rather
          than vanishing — a block that comes and goes cannot be learned. */}
      {anyAnchors.ok && anyAnchors.value ? (
        <AnchorPrompts
          prompts={anchors.ok ? anchors.value : []}
          busy={anchorBusy}
          onSnooze={(prompt) => void deferAnchor(prompt, 'snooze')}
          onNotToday={(prompt) => void deferAnchor(prompt, 'notToday')}
        />
      ) : null}

      {trend === undefined ? null : (
        <TrendCard trend={trend} unit={settings.value.weightDisplayUnit} />
      )}

      {/* Above the checklist, because this is the day's work and the checklist
          is the week's reading. A week with nothing in it renders nothing at all
          rather than an empty region (D-106 §6's reasoning, applied here). */}
      {!waiting && trainingWeek.ok && trainingWeek.value !== undefined ? (
        <SessionList week={trainingWeek.value} now={now} onChanged={reload} />
      ) : null}

      {/* Below the trend, because this is something to read rather than to do.
          Derived from the plan and the evidence the rules require — never
          configured, and inactive domains contribute nothing to it. */}
      {!waiting && checklist.ok ? <WeekChecklist checklist={checklist.value} /> : null}

      {/* Not while waiting: a missing weigh-in from before the project existed is
          not a missed day, and reporting it as one is exactly the punitive
          framing the neutral copy exists to avoid. */}
      {!waiting && daily.value.yesterdayMissing ? (
        <p class={styles.neutralNote}>
          Yesterday&rsquo;s weight is missing. Nothing needs fixing. Resume today.
        </p>
      ) : null}

      <div class={styles.planLink}>
        <Button variant="secondary" onClick={() => navigate(PLAN_ROUTE)} fullWidth>
          View my plan
        </Button>
      </div>
    </Screen>
  )
}

/**
 * The seven-day average, as a fixed-height card (D-104 §1).
 *
 * ────────────────────────────────────────────────────────────────────────────
 *  THE HEIGHT IS FIXED, AND THAT IS NOT A STYLE CHOICE
 * ────────────────────────────────────────────────────────────────────────────
 *
 * `TrendSummary` renders three to four lines depending on how much data exists,
 * so this block grew and shrank from day to day and moved everything under it.
 * C6.3's rule applies to more than the first block: *a screen whose height
 * depends on how much the user happens to have recorded cannot be learned by
 * muscle memory.* **Progress still renders the full breakdown** — that screen is
 * for reading, and the detail there is the point.
 *
 * **The direction is a word, and the arrow only repeats it.** D-086 forbids
 * colour or an icon carrying meaning alone, and a card is exactly where a
 * percentage would look natural — which is why it must not become one. The
 * change value stays inside the detail, beside the count it was computed from.
 *
 * **A missing average is not a zero.** With too few weigh-ins the card says so
 * and shows no number at all (D-036).
 */
function TrendCard({ trend, unit }: { trend: WeightTrend; unit: WeightDisplayUnit }) {
  const { current, comparison } = trend
  const weighIns = `${current.entryCount} weigh-in${current.entryCount === 1 ? '' : 's'} in the last seven days.`

  return (
    <StatusCard
      label="Seven-day average"
      {...(current.averageKg === undefined ? {} : { value: formatWeight(current.averageKg, unit) })}
      emptyLabel="Not enough weigh-ins yet"
      {...(comparison === undefined
        ? {}
        : {
            trend: {
              direction:
                comparison.changeKg < 0 ? 'down' : comparison.changeKg > 0 ? 'up' : 'level',
              word:
                comparison.changeKg === 0
                  ? 'level with last week'
                  : `${comparison.changeKg < 0 ? 'down' : 'up'} on last week`,
            },
          })}
      detailLabel="Show the numbers"
    >
      <p>{weighIns}</p>
      {current.sufficiency === 'limited' ? <p>Based on limited data this week.</p> : null}
      {comparison === undefined ? (
        current.averageKg === undefined ? null : (
          <p>Not enough data to compare with the previous seven days yet.</p>
        )
      ) : (
        <p>
          {formatWeightChange(comparison.changeKg, unit)} (
          {formatPercentChange(comparison.percentChange)}) versus the previous seven days.
        </p>
      )}
    </StatusCard>
  )
}

/**
 * The daily nutrition check — three taps and nothing more (D-096).
 *
 * ────────────────────────────────────────────────────────────────────────────
 *  THIS ASKS WHETHER A TARGET WAS MET. IT NEVER ASKS WHAT YOU ATE.
 * ────────────────────────────────────────────────────────────────────────────
 *
 * There is no number field here and there must not be one. Numeric daily intake
 * was considered and rejected: it puts a keyboard on the app's most
 * abandonment-prone screen, and the record it would write to has nowhere to put
 * the answer.
 *
 * **Three buttons, not a toggle.** `unsure` is a real answer with the same
 * evidential weight as no answer at all, and a two-state control would force "I
 * do not know" to be recorded as "no" — a count that is wrong in exactly the
 * direction that scolds the user.
 *
 * **An unanswered question is not a failure and is never styled as one.** The
 * unanswered state is plain, not red, and nothing accumulates: yesterday's blank
 * does not appear here today.
 *
 * **The heading is "Your targets", not "Today's targets".** Four specs find this
 * screen by getByRole(heading, { name: 'Today' }), and Playwright matches an
 * accessible name by substring — so a second heading containing the word made the
 * screen lookup ambiguous everywhere. This is the C6.3 "About you" collision in a
 * new costume, and the heading was renamed rather than sixteen selectors
 * tightened.
 *
 * **A closed week says so.** Once the review covering today has been finalized,
 * the answers are read-only (D-046) — and the screen states that rather than
 * appearing to accept a tap the service will refuse.
 */
function DailyNutrition({
  prompt,
  onAnswer,
}: {
  prompt: DailyNutritionPrompt
  onAnswer: (field: DailyQuestion['field'], answer: TernaryAnswer) => Promise<unknown>
}) {
  const [answers, setAnswers] = useState<Partial<Record<string, TernaryAnswer>>>(() =>
    Object.fromEntries(
      prompt.questions.filter((q) => q.answer !== undefined).map((q) => [q.field, q.answer]),
    ),
  )

  if (prompt.questions.length === 0) return null

  async function answer(field: DailyQuestion['field'], value: TernaryAnswer) {
    setAnswers((current) => ({ ...current, [field]: value }))
    await onAnswer(field, value)
  }

  return (
    <section class={styles.dailyCheck} aria-labelledby="daily-check-heading">
      <h2 id="daily-check-heading" class={styles.dailyCheckHeading}>
        Your targets
      </h2>

      {prompt.closed ? (
        <p class={styles.neutralNote}>
          This week has been reviewed, so today&rsquo;s answers can no longer be changed.
        </p>
      ) : null}

      {prompt.questions.map((question) => (
        <fieldset key={question.field} class={styles.dailyQuestion}>
          <legend class={styles.dailyQuestionLabel}>{question.label}</legend>
          <div class={styles.dailyAnswers}>
            {TERNARY_ANSWERS.map((value) => (
              <Button
                key={value}
                variant={answers[question.field] === value ? 'primary' : 'secondary'}
                disabled={prompt.closed}
                pressed={answers[question.field] === value}
                onClick={() => void answer(question.field, value)}
              >
                {TERNARY_ANSWER_LABELS[value]}
              </Button>
            ))}
          </div>
        </fieldset>
      ))}
    </section>
  )
}

/**
 * The one thing to do, and the quieter things beside it.
 *
 * **The region never disappears.** With no opinion yet it says so in one line
 * rather than vanishing, because a screen whose first block appears and
 * disappears from day to day cannot be learned — and the whole point of putting
 * it here is that the user knows where to look without reading.
 *
 * The supporting actions are already capped at two by the combiner, and the
 * domains Project Sola is not judging are named rather than omitted: silence
 * about an area the user chose reads as approval.
 */
function NextAction({ combined }: { combined: CombinedOpinion | undefined }) {
  if (combined === undefined) {
    return (
      <section class={styles.nextAction} aria-labelledby="next-action-heading">
        <h2 id="next-action-heading" class={styles.nextActionHeading}>
          This week
        </h2>
        <p class={styles.nextActionQuiet}>
          Project Sola could not read this week just now. Your entries are unaffected.
        </p>
      </section>
    )
  }

  return (
    <section class={styles.nextAction} aria-labelledby="next-action-heading">
      <h2 id="next-action-heading" class={styles.nextActionHeading}>
        This week
      </h2>

      {combined.status === 'opinion' ? (
        <p class={styles.nextActionPrimary}>{combined.primaryAction}</p>
      ) : (
        <p class={styles.nextActionQuiet}>
          Nothing to change this week. Keep recording, and the weekly review is where Project Sola
          takes a proper look.
        </p>
      )}

      {combined.supportingActions.length === 0 ? null : (
        <ul class={styles.nextActionSupporting}>
          {combined.supportingActions.map((action) => (
            <li key={action}>{action}</li>
          ))}
        </ul>
      )}

      {combined.quiet.length === 0 ? null : (
        <p class={styles.nextActionQuiet}>
          Not judged yet:{' '}
          {combined.quiet
            .map((entry) => FITNESS_DOMAIN_LABELS[entry.domain].toLowerCase())
            .join(', ')}
          .
        </p>
      )}
    </section>
  )
}

function OnboardingPrompt() {
  const [, navigate] = useLocation()
  return (
    <Screen title="Today" headerAccessory={<SolaMark size="sm" />}>
      {/* No phase in this list. D-063 removed the phase question from setup —
          the user gives a destination and the route engine decides — so naming
          it here promised a question that has not been asked since. */}
      <p class={styles.lede}>
        Project Sola needs a few details before it can interpret anything: your height, your date of
        birth, what you want to work on, and the date you are working towards.
      </p>
      <Button onClick={() => navigate('/onboarding')}>Set up Project Sola</Button>
    </Screen>
  )
}

interface WeightEntryProps {
  existingKg: Kilograms | undefined
  unit: WeightDisplayUnit
  onSave: (weightKg: number) => Promise<Result<WeightEntryRecord, StorageError>>
}

/**
 * The daily weight field.
 *
 * Pre-filled with today's saved value when one exists, so a correction is an
 * edit rather than a re-entry. Saving the same date overwrites (D-035).
 *
 * The input is stored as a string and converted once on save. There is no
 * shorthand interpretation of `684` as `68.4` — D-035 declines it.
 */
function WeightEntry({ existingKg, unit, onSave }: WeightEntryProps) {
  const initial = existingKg === undefined ? '' : formatWeightValue(existingKg, unit)
  const [value, setValue] = useState(initial)
  const [error, setError] = useState<string | undefined>(undefined)
  const [saved, setSaved] = useState(false)
  const [saving, setSaving] = useState(false)

  async function save() {
    const trimmed = value.trim()
    if (trimmed === '') {
      setError('Enter your weight to save it.')
      return
    }
    const parsed = Number(trimmed)
    if (!Number.isFinite(parsed)) {
      setError('Weight must be a number, for example 79.4.')
      return
    }
    // Display units are converted here, at the boundary. Storage stays canonical
    // kilograms regardless of what is shown (D-017, D-041).
    const kilograms = unit === 'kg' ? parsed : parsed * 0.45359237

    setSaving(true)
    setError(undefined)
    const result = await onSave(kilograms)
    setSaving(false)

    if (!result.ok) {
      setError(result.error.message)
      return
    }
    setSaved(true)
  }

  return (
    <div class={styles.entry}>
      <NumericField
        label={existingKg === undefined ? "Today's weight" : "Today's weight (saved)"}
        suffix={unit}
        placeholder={unit === 'kg' ? '79.4' : '175.0'}
        value={value}
        autoFocus={existingKg === undefined}
        onInput={(next) => {
          setValue(next)
          setSaved(false)
          setError(undefined)
        }}
        {...(error === undefined ? {} : { error })}
      />
      <Button onClick={() => void save()} busy={saving} fullWidth>
        {existingKg === undefined ? 'Save weight' : 'Update weight'}
      </Button>
      {/* Confirmation is text, announced politely — never colour alone. */}
      <p class={styles.savedNote} role="status">
        {saved ? 'Saved.' : ''}
      </p>
    </div>
  )
}
